import "./application.css"
import React from "react"
import { createInertiaApp, router } from "@inertiajs/react"
import { createRoot, hydrateRoot } from "react-dom/client"
import MarketingLayout from "@/components/MarketingLayout"
import AppLayout from "@/components/AppLayout"
import AuthLayout from "@/components/AuthLayout"
import AdminLayout from "@/components/AdminLayout"
import type { AnalyticsConfig } from "@/lib/analytics/types"
import type { AuthShare } from "@/lib/inertia"
import { syncChatIdentity } from "@/lib/chat"

type PageModule = {
  default: React.ComponentType<unknown> & {
    layout?: (page: React.ReactNode) => React.ReactNode
  }
}
type SentryModule = typeof import("@/lib/sentry")

let sentryModule: Promise<SentryModule> | null = null

function loadSentry() {
  sentryModule ||= import("@/lib/sentry")
  return sentryModule
}

function bootTelemetry(analytics?: AnalyticsConfig) {
  if (!analytics) return

  if (analytics.sentry) {
    void loadSentry().then(({ mountSentry, identifySentry }) => {
      mountSentry(analytics.sentry)
      identifySentry(analytics.identify)
    })
  }

  if (analytics.enabled) {
    void import("@/lib/analytics").then(({ mountAnalytics }) => {
      mountAnalytics(analytics)
    })
  }
}

function syncSentryIdentity(analytics?: AnalyticsConfig) {
  if (analytics?.sentry) {
    void loadSentry().then(({ mountSentry, identifySentry }) => {
      mountSentry(analytics.sentry)
      identifySentry(analytics.identify)
    })
  } else if (sentryModule) {
    void sentryModule.then(({ identifySentry }) => identifySentry(analytics?.identify ?? null))
  }
}

const pages = import.meta.glob<PageModule>("../pages/**/*.tsx", { eager: false })

function defaultLayoutFor(name: string) {
  if (name.startsWith("Admin/")) {
    return (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>
  }
  if (name.startsWith("App/")) {
    return (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
  }
  if (
    name.startsWith("Auth/") ||
    name === "Onboarding/Index" ||
    name.startsWith("Invitations/") ||
    name.startsWith("Public/") ||
    name.startsWith("Appsumo/")
  ) {
    return (page: React.ReactNode) => <AuthLayout>{page}</AuthLayout>
  }
  return (page: React.ReactNode) => <MarketingLayout>{page}</MarketingLayout>
}

createInertiaApp({
  resolve: async (name) => {
    const importer = pages[`../pages/${name}.tsx`]
    if (!importer) throw new Error(`Inertia page not found: ${name}`)
    const mod = await importer()
    const Page = mod.default
    if (!Page.layout) {
      Page.layout = defaultLayoutFor(name)
    }
    return Page
  },
  setup({ el, App, props }) {
    const pageProps = props.initialPage.props as { analytics?: AnalyticsConfig; auth?: AuthShare }
    bootTelemetry(pageProps.analytics)
    syncChatIdentity(pageProps.auth)

    if (el.hasChildNodes()) {
      hydrateRoot(el, <App {...props} />)
    } else {
      createRoot(el).render(<App {...props} />)
    }
  },
  progress: {
    color: "oklch(0.58 0.16 158)",
    showSpinner: false,
  },
})

// Keep Sentry's and Sonny's user context in sync after Inertia navigations.
router.on("navigate", (e) => {
  const pageProps = e.detail.page.props as { analytics?: AnalyticsConfig; auth?: AuthShare }
  syncSentryIdentity(pageProps.analytics)
  syncChatIdentity(pageProps.auth)
})
